import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Search, Users, PlusCircle, Trash2, Loader2, ArrowRight, User,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function PatientsListPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);

    const canDelete = user && (user.role === "doctor" || user.role === "admin");
    const canCreate = user && (user.role === "doctor" || user.role === "admin");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/patients");
            setPatients(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return patients;
        return patients.filter((p) =>
            (p.full_name || "").toLowerCase().includes(term)
            || (p.cpf || "").toLowerCase().includes(term)
            || (p.email || "").toLowerCase().includes(term)
            || (p.phone || "").toLowerCase().includes(term)
        );
    }, [patients, q]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        if (confirmText.trim() !== deleteTarget.full_name.trim()) {
            toast.error("Digite o nome exato do paciente para confirmar.");
            return;
        }
        setDeleting(true);
        try {
            const { data } = await api.delete(`/patients/${deleteTarget.id}`);
            const d = data.deleted || {};
            toast.success(
                `Paciente excluído. Registros removidos: ${d.consultations || 0} consultas, ${d.care_plans || 0} planos.`,
                { duration: 6000 }
            );
            setDeleteTarget(null);
            setConfirmText("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleting(false); }
    };

    return (
        <AppShell>
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">Clínica</div>
                    <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand">Pacientes cadastrados</h1>
                    <p className="text-sm text-muted-clinical mt-1">
                        {loading ? "Carregando..." : `${patients.length} pacientes na clínica${q ? ` · ${filtered.length} filtrados` : ""}`}
                    </p>
                </div>
                {canCreate && (
                    <Link to="/patients/new" data-testid="new-patient-btn" className="btn-primary">
                        <PlusCircle size={14} /> Novo paciente
                    </Link>
                )}
            </div>

            <div className="card-earthy mb-4">
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-clinical" />
                    <input
                        data-testid="patients-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar por nome, CPF, e-mail ou telefone..."
                        className="input-clinical pl-9"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand" size={32} /></div>
            ) : filtered.length === 0 ? (
                <div className="card-earthy text-center text-sm text-muted-clinical py-14" data-testid="patients-empty">
                    <Users size={32} className="text-brand mx-auto mb-3 opacity-60" />
                    {q ? "Nenhum paciente encontrado com este termo." : "Nenhum paciente cadastrado ainda."}
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((p) => (
                        <PatientRow
                            key={p.id}
                            p={p}
                            canDelete={canDelete}
                            onOpen={() => navigate(`/patients/${p.id}`)}
                            onDelete={() => { setDeleteTarget(p); setConfirmText(""); }}
                        />
                    ))}
                </div>
            )}

            {deleteTarget && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delete-patient-modal">
                    <div className="card-earthy max-w-md w-full space-y-4 bg-white">
                        <div className="flex items-center gap-3 text-[var(--status-danger)]">
                            <Trash2 size={24} />
                            <h2 className="font-display text-xl font-medium">Excluir paciente</h2>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)]">
                            Esta ação é <strong>irreversível</strong>. Serão apagados o cadastro de{" "}
                            <strong className="text-brand">{deleteTarget.full_name}</strong> e todos os dados
                            relacionados: consultas, avaliações AGA, planos de cuidado, evoluções, lembretes,
                            vínculos de cuidador e ciclos de faturamento.
                        </p>
                        <div>
                            <label className="label-clinical">Para confirmar, digite o nome completo do paciente:</label>
                            <input
                                data-testid="delete-patient-confirm-input"
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder={deleteTarget.full_name}
                                className="input-clinical"
                                autoFocus
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setDeleteTarget(null)} className="btn-ghost" disabled={deleting}>
                                Cancelar
                            </button>
                            <button
                                data-testid="delete-patient-confirm-btn"
                                onClick={handleDelete}
                                disabled={deleting || confirmText.trim() !== deleteTarget.full_name.trim()}
                                className="px-4 py-2 rounded-lg bg-[var(--status-danger)] text-white font-medium hover:bg-[#7a1f21] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                {deleting ? "Excluindo..." : "Excluir definitivamente"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AppShell>
    );
}

function PatientRow({ p, canDelete, onOpen, onDelete }) {
    return (
        <div data-testid={`patient-row-${p.id}`} className="card-earthy hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-full bg-alt text-brand flex items-center justify-center font-semibold shrink-0">
                    <User size={16} />
                </div>
                <button
                    onClick={onOpen}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`patient-open-${p.id}`}
                >
                    <div className="font-medium text-brand truncate">{p.full_name || "—"}</div>
                    <div className="text-xs text-muted-clinical truncate">
                        {[p.birth_date, p.sex, p.insurance, p.city_uf].filter(Boolean).join(" · ") || "Sem dados demográficos"}
                    </div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={onOpen}
                        data-testid={`patient-view-${p.id}`}
                        className="btn-ghost text-xs"
                    >
                        Abrir <ArrowRight size={12} />
                    </button>
                    {canDelete && (
                        <button
                            onClick={onDelete}
                            data-testid={`patient-delete-${p.id}`}
                            className="text-xs px-2.5 py-1.5 rounded-lg text-[var(--status-danger)] hover:bg-[var(--status-danger-bg)] transition-colors flex items-center gap-1"
                            title="Excluir paciente"
                        >
                            <Trash2 size={12} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
